import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { RestaurantWizardStep } from '../../constants/RestaurantWizardConstants';
import { getWizardStepPath } from '../../pages/MainContainer/RestaurantWizard/restaurantWizardNavigation';
import {
  RESTAURANT_DROPDOWN_HIDDEN_MATCHER,
  SIDEBAR_NAV_CONFIG,
  SidebarNavId,
  SidebarRouteMatcher
} from './sidebarNavConfig';

const matchesPathPrefix = (pathname: string, segment: string) =>
  pathname === `/${segment}` || pathname.startsWith(`/${segment}/`);

export const matchesSidebarRoute = (pathname: string, matcher: SidebarRouteMatcher): boolean =>
  [
    matcher.test?.(pathname),
    matcher.exactPaths?.some((path) => pathname === path),
    matcher.paths?.some((segment) => matchesPathPrefix(pathname, segment)),
    matcher.includes?.some((segment) => pathname.includes(segment))
  ].some(Boolean);

const sidebarNavIds = Object.keys(SIDEBAR_NAV_CONFIG) as SidebarNavId[];

const isDirectSidebarNavMatch = (pathname: string, id: SidebarNavId): boolean => {
  const { match } = SIDEBAR_NAV_CONFIG[id];
  return !match.anyOf && matchesSidebarRoute(pathname, match);
};

const isParentSidebarNavActive = (id: SidebarNavId, active: Record<SidebarNavId, boolean>): boolean =>
  SIDEBAR_NAV_CONFIG[id].match.anyOf?.some((childId) => active[childId]) ?? false;

const getDirectSidebarNavActive = (pathname: string): Record<SidebarNavId, boolean> =>
  Object.fromEntries(sidebarNavIds.map((id) => [id, isDirectSidebarNavMatch(pathname, id)])) as Record<
    SidebarNavId,
    boolean
  >;

const resolveParentSidebarNavActive = (directActive: Record<SidebarNavId, boolean>): Record<SidebarNavId, boolean> =>
  Object.fromEntries(
    sidebarNavIds.map((id) => [id, directActive[id] || isParentSidebarNavActive(id, directActive)])
  ) as Record<SidebarNavId, boolean>;

export const getSidebarNavActive = (pathname: string): Record<SidebarNavId, boolean> =>
  resolveParentSidebarNavActive(getDirectSidebarNavActive(pathname));

export const useSidebarNavigation = () => {
  const { pathname } = useLocation();
  return useMemo(() => getSidebarNavActive(pathname), [pathname]);
};

export const isSettingsAccountRoute = (pathname: string) => getSidebarNavActive(pathname).settings;

export const isRestaurantProfileStepActive = (pathname: string, step: RestaurantWizardStep) =>
  pathname === getWizardStepPath(true, step);

export const isLocationProfileSubmenuExpanded = (pathname: string): boolean =>
  getSidebarNavActive(pathname).locationProfile;

export const isSidebarSubmenuExpanded = (
  pathname: string,
  section: 'locationProfile' | 'brandProfile' | 'marketing' | 'design'
): boolean => {
  switch (section) {
    case 'locationProfile':
      return isLocationProfileSubmenuExpanded(pathname);
    case 'brandProfile':
      return getSidebarNavActive(pathname).brandProfile;
    case 'marketing':
      return getSidebarNavActive(pathname).marketing;
    case 'design':
      return getSidebarNavActive(pathname).design;
    default: {
      const unhandled: never = section;
      return unhandled;
    }
  }
};

export const isRestaurantProfileSidebarItemActive = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

export const isRestaurantDropdownHidden = (pathname: string) =>
  matchesSidebarRoute(pathname, RESTAURANT_DROPDOWN_HIDDEN_MATCHER);
